import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { createCustomer } from "../api/customersApi";

const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  addressLine: "",
  city: "",
  state: "",
  country: "US",
  zipCode: "",
  password: "",
  confirmPassword: "",
  utilityCompanyName: "",
  utilityAccountNumber: "",
};

function validateFields(formData) {
  const errors = {};

  if (!formData.firstName) {
    errors.firstName = "First name is required";
  } else if (!/^[a-zA-Z\s'-]{1,50}$/.test(formData.firstName)) {
    errors.firstName = "First name can only contain letters, spaces, hyphens, or apostrophes (max 50 characters).";
  }

  if (!formData.lastName) {
    errors.lastName = "Last name is required";
  } else if (!/^[a-zA-Z\s'-]{1,50}$/.test(formData.lastName)) {
    errors.lastName = "Last name can only contain letters, spaces, hyphens, or apostrophes (max 50 characters).";
  }

  if (!formData.email) {
    errors.email = "Email is required";
  } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)) {
    errors.email = "Invalid email format.";
  } else if (formData.email.length > 256) {
    errors.email = "Email cannot exceed 256 characters.";
  }

  if (!formData.phone) {
    errors.phone = "Phone number is required";
  } else if (!/^\d{10}$/.test(formData.phone)) {
    errors.phone = "US phone number must be 10 digits.";
  }

  if (!formData.addressLine) {
    errors.addressLine = "Address line is required";
  } else if (formData.addressLine.length > 50) {
    errors.addressLine = "Address line must not exceed 50 characters";
  }

  if (!formData.city) {
    errors.city = "City is required";
  }

  if (!formData.state) {
    errors.state = "State is required";
  }

  if (!formData.zipCode) {
    errors.zipCode = "Zip code is required";
  } else if (!/^\d{5}$/.test(formData.zipCode)) {
    errors.zipCode = "Zip code must be exactly 5 digits";
  }

  if (!formData.password) {
    errors.password = "Password is required";
  } else {
    const { password } = formData;
    const lower = /[a-z]/.test(password);
    const upper = /[A-Z]/.test(password);
    const digit = /\d/.test(password);
    const special = /[@$!%*?&]/.test(password);
    const complexityCount = [lower, upper, digit, special].filter(Boolean).length;

    if (password.length < 8 || complexityCount < 3) {
      errors.password = "Password must be at least 8 characters long and include at least 3 of the following: uppercase, lowercase, number, special character.";
    }
  }

  if (!formData.confirmPassword) {
    errors.confirmPassword = "Confirm password is required";
  } else if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }

  return errors;
}

export default function useAddCustomer({ accessToken, userId, branchId, onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = useCallback(() => setShowModal(true), []);
  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setSubmitError("");
  }, []);

  const handleChange = useCallback((e) => {
    const { id, name, value } = e.target;
    const key = name || id;
    if (!key) return;
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitError("");

      const validationErrors = validateFields(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        await createCustomer({ accessToken, userId, branchId, formData });
        toast.success("Customer created successfully");
        closeModal();
        onSuccess?.();
      } catch (err) {
        const msg = err.message || "Failed to create customer. Please try again.";
        setSubmitError(msg);
        toast.error(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, accessToken, userId, branchId, closeModal, onSuccess],
  );

  return {
    showModal,
    openModal,
    closeModal,
    formData,
    errors,
    submitError,
    isSubmitting,
    handleChange,
    handleSubmit,
  };
}
